/**
 * Workspace · Petty Cash replenish composer (S35).
 *
 * 2-col composer that posts a `cash_movements` row with `kind=transfer`,
 * `fromAccountId=<bank>`, `toAccountId=<drawer>`. The right pane is the
 * shared `<TransferComposerPreview>` pre-classified as BANK→CASH (petty
 * cash top-up). Quick-fill chips default to "Top up to imprest" — the
 * `imprestPaise - balancePaise` gap that brings the float back to ceiling.
 *
 * @see prototype/cash/petty-cash-replenish.html
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10.2
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check } from "lucide-react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import type { pettyCash as pettyCashContract } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { TransferComposerPreview, type GuardCheck } from "../../../../features/transfers/index.ts";
import { paiseToInr, rupeesToPaiseString } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/cash/petty-cash/replenish")({
  component: ReplenishPage,
});

/** Subset of `cashMovements.post` we read. */
interface PostMovementResult {
  awaitingApproval: { summary: string } | null;
}

function ReplenishPage() {
  const anchorQ = trpc.pettyCash.anchorData.useQuery({});
  const accountsQ = trpc.bankAccounts.list.useQuery({});

  if (anchorQ.isLoading || accountsQ.isLoading) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Replenish" },
        ]}
      >
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading composer…</p>
          </CardBody>
        </Card>
      </WorkspaceShell>
    );
  }
  const firstError = anchorQ.error ?? accountsQ.error;
  if (firstError || !anchorQ.data || !accountsQ.data) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Replenish" },
        ]}
      >
        <Alert
          intent="bad"
          title="Couldn't load composer"
          description={firstError?.message ?? "Missing data"}
        />
      </WorkspaceShell>
    );
  }

  const bankRows = (accountsQ.data.rows as ReadonlyArray<BankAccountWire>).filter(
    (r: BankAccountWire) => r.type === "bank" && r.archivedAt === null,
  );

  return <ReplenishForm anchor={anchorQ.data} bankAccounts={bankRows} />;
}

interface BankAccountWire {
  id: string;
  name: string;
  type: "bank" | "cash" | "credit_card";
  runningBalancePaise: string;
  archivedAt: string | null;
}

type AnchorData = pettyCashContract.AnchorDataOutput;

type QuickFill = "top-up" | "full" | "custom";

function ReplenishForm({
  anchor,
  bankAccounts,
}: {
  anchor: AnchorData;
  bankAccounts: ReadonlyArray<BankAccountWire>;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const drawer = anchor.drawer;
  const balancePaise = BigInt(drawer.balancePaise);
  const imprestPaise = BigInt(drawer.imprestPaise);

  // Suggested top-up = imprest - balance (clamped at 0).
  const topUpPaise = imprestPaise > balancePaise ? imprestPaise - balancePaise : 0n;

  const [fromAccountId, setFromAccountId] = useState<string>(bankAccounts[0]?.id ?? "");
  const [quickFill, setQuickFill] = useState<QuickFill>("top-up");
  const [amountRupees, setAmountRupees] = useState(paiseToRupeesInputBare(topUpPaise));
  const [valueDate, setValueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState(`Petty cash top-up — ${drawer.name}`);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const mutation = trpc.cashMovements.post.useMutation({
    onSuccess: async (res: PostMovementResult) => {
      // Refresh drawer + bank balances + activity + briefings.
      await Promise.all([
        utils.pettyCash.anchorData.invalidate(),
        utils.cashMovements.list.invalidate(),
        utils.bankAccounts.list.invalidate(),
        utils.briefings.workspaceSignals.invalidate(),
        utils.briefings.today.invalidate(),
      ]);
      if (res.awaitingApproval) {
        setInfo(
          `Queued for founder review (${res.awaitingApproval.summary}). Tracked in the approvals inbox.`,
        );
      } else {
        void navigate({ to: "/cash/petty-cash" });
      }
    },
    onError: (err: { message: string }) => setError(err.message),
  });

  function applyQuickFill(next: QuickFill) {
    setQuickFill(next);
    if (next === "top-up") setAmountRupees(paiseToRupeesInputBare(topUpPaise));
    else if (next === "full") setAmountRupees(paiseToRupeesInputBare(imprestPaise));
    // custom: leave the field as-is
  }

  const amountPaiseStr = rupeesToPaiseString(amountRupees);
  const amountPaise = amountPaiseStr ? BigInt(amountPaiseStr) : 0n;

  const fromAccount = useMemo(
    () => bankAccounts.find((a) => a.id === fromAccountId),
    [bankAccounts, fromAccountId],
  );

  function submit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!fromAccountId) {
      setError("Pick the bank account to draw from.");
      return;
    }
    if (amountPaise <= 0n) {
      setError("Enter an amount greater than zero.");
      return;
    }
    if (narration.trim().length === 0) {
      setError("Add a narration so the auditor knows why this top-up landed.");
      return;
    }
    mutation.mutate({
      kind: "transfer",
      fromAccountId,
      toAccountId: drawer.id,
      amountPaise: amountPaise.toString(),
      valueDate,
      narration: narration.trim(),
    });
  }

  if (bankAccounts.length === 0) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Replenish" },
        ]}
      >
        <Alert
          intent="warn"
          title="No bank account on file"
          description="Add a bank account before topping up the drawer."
        />
      </WorkspaceShell>
    );
  }

  const fromBeforePaise = BigInt(fromAccount?.runningBalancePaise ?? "0");
  const fromAfterPaise = fromBeforePaise - amountPaise;
  const drawerAfterPaise = balancePaise + amountPaise;

  const maxBarPaise =
    fromBeforePaise > imprestPaise + amountPaise ? fromBeforePaise : imprestPaise + amountPaise;
  const barPct = (n: bigint) =>
    maxBarPaise === 0n ? 0 : Math.max(0, Math.min(100, Number((n * 100n) / maxBarPaise)));

  const guards: GuardCheck[] = [];
  if (fromAfterPaise < 0n) {
    guards.push({
      status: "bad",
      title: "Bank goes negative",
      detail: "The from-account would go below zero after this transfer.",
    });
  } else {
    guards.push({
      status: "ok",
      title: "Source covers it",
      detail: `${fromAccount?.name ?? "Bank"} holds ${paiseToInr(fromBeforePaise)} — comfortable cover.`,
    });
  }
  if (drawerAfterPaise > imprestPaise) {
    guards.push({
      status: "warn",
      title: "Above imprest ceiling",
      detail: `Top-up would push drawer to ${paiseToInr(drawerAfterPaise)} — above the ${paiseToInr(imprestPaise)} ceiling.`,
    });
  }

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Petty cash", to: "/cash/petty-cash" },
        { label: "Replenish" },
      ]}
    >
      <div className="flex flex-col gap-5">
        <header className="flex flex-wrap items-start gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/cash/petty-cash"
              className="inline-grid place-items-center w-9 h-9 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg no-underline"
              aria-label="Back to petty cash"
            >
              <ArrowLeft size={16} strokeWidth={2} />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
                Replenish drawer
              </h1>
              <p className="mt-1 text-xs text-muted">
                {drawer.name} · {paiseToInr(drawer.balancePaise)} available · Imprest{" "}
                {paiseToInr(drawer.imprestPaise)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button asChild intent="ghost" size="md" className="border border-line">
              <Link to="/cash/petty-cash">Cancel</Link>
            </Button>
            <Button
              type="button"
              intent="primary"
              size="md"
              onClick={submit}
              disabled={mutation.isPending}
            >
              <Check size={13} strokeWidth={2.4} aria-hidden />
              {mutation.isPending ? "Posting…" : "Post replenishment"}
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5">
          {/* LEFT — form */}
          <form onSubmit={submit} className="flex flex-col gap-5 min-w-0">
            <section className="bg-paper border border-line rounded-md shadow-sm p-5 flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-ink">From bank</h3>
              <Select value={fromAccountId} onValueChange={setFromAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a bank account…" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} · {paiseToInr(a.runningBalancePaise)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </section>

            <section className="bg-paper border border-line rounded-md shadow-sm p-5 flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-ink">Amount</h3>
              <div className="flex flex-wrap gap-2">
                <QuickFillChip
                  active={quickFill === "top-up"}
                  onClick={() => applyQuickFill("top-up")}
                  disabled={topUpPaise === 0n}
                >
                  Top up to imprest · {paiseToInr(topUpPaise)}
                </QuickFillChip>
                <QuickFillChip active={quickFill === "full"} onClick={() => applyQuickFill("full")}>
                  Full imprest · {paiseToInr(imprestPaise)}
                </QuickFillChip>
                <QuickFillChip
                  active={quickFill === "custom"}
                  onClick={() => applyQuickFill("custom")}
                >
                  Custom
                </QuickFillChip>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-ink-2">
                    Amount <span className="text-rose-600">*</span>
                  </span>
                  <div className="flex items-center bg-paper border border-line rounded-sm focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100 overflow-hidden">
                    <span aria-hidden className="px-2 text-muted text-sm">
                      ₹
                    </span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={amountRupees}
                      onChange={(e) => {
                        setAmountRupees(e.target.value);
                        setQuickFill("custom");
                      }}
                      className="border-0 focus-visible:ring-0"
                      aria-label="Amount in rupees"
                    />
                  </div>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-ink-2">
                    Value date <span className="text-rose-600">*</span>
                  </span>
                  <Input
                    type="date"
                    value={valueDate}
                    onChange={(e) => setValueDate(e.target.value)}
                  />
                </label>
              </div>
            </section>

            <section className="bg-paper border border-line rounded-md shadow-sm p-5 flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-ink">Narration</h3>
              <textarea
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                rows={2}
                required
                className="w-full px-3 py-2 rounded-sm border border-line bg-paper text-sm text-ink placeholder:text-muted focus:border-sky-600 focus:ring-2 focus:ring-sky-100 outline-none"
              />
            </section>

            {error ? (
              <Alert intent="bad" title="Couldn't post the replenishment" description={error} />
            ) : null}
            {info ? <Alert intent="info" title="Heads up" description={info} /> : null}
          </form>

          {/* RIGHT — preview */}
          <aside className="lg:sticky lg:top-4 self-start">
            <TransferComposerPreview
              subtype="bank_to_cash"
              subtypeSummary="Bank → Cash · brings the drawer float back to the imprest ceiling. Posts as a single transfer row with no GL net (asset shuffle)."
              fromAccountName={fromAccount?.name ?? "—"}
              toAccountName={drawer.name}
              amountPaise={amountPaise.toString()}
              postingLines={[
                {
                  side: "Dr",
                  account: drawer.name,
                  accountClass: "Asset · Cash",
                  amountPaise: amountPaise.toString(),
                },
                {
                  side: "Cr",
                  account: fromAccount?.name ?? "Bank",
                  accountClass: "Asset · Bank",
                  amountPaise: amountPaise.toString(),
                },
              ]}
              fromProjection={{
                name: fromAccount?.name ?? "—",
                type: "bank",
                beforePaise: fromBeforePaise.toString(),
                afterPaise: fromAfterPaise.toString(),
                beforeBarPct: barPct(fromBeforePaise),
                afterBarPct: barPct(fromAfterPaise < 0n ? 0n : fromAfterPaise),
              }}
              toProjection={{
                name: drawer.name,
                type: "cash",
                beforePaise: balancePaise.toString(),
                afterPaise: drawerAfterPaise.toString(),
                beforeBarPct: barPct(balancePaise),
                afterBarPct: barPct(drawerAfterPaise),
              }}
              guards={guards}
            />
          </aside>
        </div>
      </div>
    </WorkspaceShell>
  );
}

function QuickFillChip({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "inline-flex items-center px-2.5 py-1.5 rounded-sm border text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
        (active
          ? "bg-sky-50 border-sky-600 text-sky-700"
          : "bg-paper border-line text-ink-2 hover:bg-raised")
      }
    >
      {children}
    </button>
  );
}

/**
 * Local copy of `paiseToRupeesInput` returning the bare numeric string for
 * a controlled `<input>` field — drops the `₹`/decimal cosmetic so users can
 * type directly.
 */
function paiseToRupeesInputBare(paise: bigint): string {
  const rupees = paise / 100n;
  const remainder = paise % 100n;
  if (remainder === 0n) return rupees.toString();
  return `${rupees.toString()}.${remainder.toString().padStart(2, "0")}`;
}
